import { Alert, Button, Col, Divider, Row, Spin, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Card from '../../../components/Card/Card';
import './CustomerPersona.css';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchRequest } from '../../../utils/Fetcher';
import { selectSessionId } from '../../../store/sessionSlice';
import { SaveProgress } from '../../../utils/SaveProgress';


const CustomerPersona = ({ onNext }) => {
  const desc = "Choose one customer persona";

  const [cardContent, setCardContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState("");
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [promptById, setPromptById] = useState({});
  const [revisingId, setRevisingId] = useState(null);

  const sessionId = useSelector(selectSessionId);

  const onButtonClick = async () => {
    await SaveProgress({
      sessionId: sessionId,
      method: "POST",
      param: `customer-persona/save`,
      payload: {
          selectedIds: [selectedId]
      }
    });
    
    onNext(selectedId);
  }

  const postGenerateCustPersona = async () => {
    setLoadingDesc("AI is generating your customer persona, please wait...");
    const { result } = await fetchRequest({
      method: "POST",
      path: `sessions/customer-persona/generate`,
      data: JSON.stringify({ numOfPrompts: 3 }),
    });
    return result;
  };

  const getCustPersona = async () => {
    setLoadingDesc("Fetching customer persona...");
    const { result } = await fetchRequest({
      method: "GET",
      path: `sessions/customer-persona`,
    });
    return result;
  };

  const handleRevise = async (personaId) => {
    const instruction = (promptById[personaId] || "").trim();
    if (!instruction) return;

    try {
      setRevisingId(personaId);

      const { result } = await fetchRequest({
        method: "PATCH",
        path: `sessions/customer-persona/${personaId}/revise`,
        data: JSON.stringify({ revision_instructions: instruction }),
      });

      const updated = result?.data ?? result;

      setCardContent((prev) =>
        prev.map((p) =>
          p.id === personaId
            ? { ...p, personaName: updated.personaName, personaBio: updated.personaBio }
            : p
        )
      );

      setPromptById((prev) => ({ ...prev, [personaId]: "" }));
    } finally {
      setRevisingId(null);
    }
  };

  const normalizeList = (payload) => {
    const list = payload?.data ?? payload?.items ?? payload;
    return Array.isArray(list) ? list : [];
  };

  const keepSelectedIfExists = (list, prevSelectedId) =>
    list.some((x) => x.id === prevSelectedId) ? prevSelectedId : null;

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const existing = normalizeList(await getCustPersona());

        if (cancelled) return;

        if (existing.length > 0) {
          setCardContent(existing);
          setSelectedId((prev) => keepSelectedIfExists(existing, prev));
          return;
        }

        await postGenerateCustPersona();
        if (cancelled) return;

        const generated = normalizeList(await getCustPersona());
        if (cancelled) return;

        setCardContent(generated);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingDesc("")
        };
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const custPersonaContent = (title, bio) => {
    return (
      <div>
        <h4 className="persona-title">{title}</h4>
        <div>{bio}</div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-title-container">
        <div className="content-title">
          {desc}
          <Tooltip placement="bottom" title={process.env.REACT_APP_TOOLTIP_CUSTOMER_PERSONA}>
            <QuestionCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#888', cursor: 'pointer' }} />
          </Tooltip>
        </div>
        <Divider className="content-title-divider" />
      </div>

       {error && (
        <Alert
          type="error"
          showIcon
          title="Couldn’t load customer personas"
          description={error}
          style={{ margin: 20 }}
        />
      )}

      {loading ? (
        <div className='loading-container'>
          {loadingDesc}
          <Spin />
        </div>
      ) : (
        <Row gutter={[10, 10]} className="customer-persona-container">
          {cardContent.map((item) => (
            <Col xs={24} sm={12} md={8} key={item.id}>
              <Card
                key={item.id}
                content={custPersonaContent(item.personaName, item.personaBio)}
                isSelected={selectedId === item.id}
                hidePrompt={selectedId !== null} // sesuai behavior kamu sekarang

                onSelect={() => setSelectedId(prev => (prev === item.id ? null : item.id))}

                promptValue={promptById[item.id] || ""}
                onPromptChange={(v) => setPromptById(prev => ({ ...prev, [item.id]: v }))}
                revising={revisingId === item.id}
                onRevise={() => handleRevise(item.id)}
              />
            </Col>
          ))}
        </Row>
      )}

      {selectedId !== null && <div className="back-next-button-cp">
        <Button disabled={!selectedId} onClick={onButtonClick}>
          Next
        </Button>
      </div>}
    </div>
  );
};


export default CustomerPersona;